import { ReactElement } from 'react';
import { TEST_IDS } from '@localstack/constants';
import { Alert, Card, CardContent, CardActions, Stack } from '@mui/material';
import { useRoutes } from '@localstack/services';
import { LambdaWebFunctionCreateRequest, LambdaWebFunctionCreateOptions } from '@localstack/types';
import { LambdaWebFunctionCreateForm, PageTitle, ProgressButton } from '@localstack/ui';

import { LambdaProps } from './types';
import { useWebFunctionCreate } from './hooks/useWebFunctionCreate';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';

const FORM_ID = 'CreateLambdaWebFunction';

export const LambdaWebFunctionCreate = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebFunctionCreateContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebFunctionCreateContent = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { goto } = useRoutes();
  const { create, isLoading, hasError, preparedResources } = useWebFunctionCreate(clientOverrides);
  const handleCreate = async (data: LambdaWebFunctionCreateRequest, options: LambdaWebFunctionCreateOptions) => {
    const name = await create(data, options);
    if (name) goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name });
  };

  return (
    <Layout
      documentTitle="Lambda: Create Web Function"
      title={
        <PageTitle
          title="Create Web Function"
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTIONS)],
            ['Create', null],
          ]}
        />
      }
      actions={
        <ProgressButton form={FORM_ID} type="submit" variant="outlined" color="primary" loading={isLoading}>
          Submit
        </ProgressButton>
      }
    >
      <Stack spacing={3}>
        {!isLoading && hasError && (preparedResources?.bucket || preparedResources?.roleArn) && (
          <Alert severity="info">
            Supporting resources are retained for retry: {preparedResources.bucket} {preparedResources.roleArn}. If you
            cancel, you can remove unused resources from the S3 and IAM browsers.
          </Alert>
        )}
        <Card>
          <CardContent>
            <LambdaWebFunctionCreateForm
              formId={FORM_ID}
              loading={isLoading}
              clientOverrides={clientOverrides}
              onSubmit={handleCreate}
            />
          </CardContent>
          <CardActions>
            <ProgressButton
              data-testid={TEST_IDS.LAMBDA_WEB_FORM_SUBMIT}
              form={FORM_ID}
              type="submit"
              variant="contained"
              color="primary"
              loading={isLoading}
            >
              Submit
            </ProgressButton>
          </CardActions>
        </Card>
      </Stack>
    </Layout>
  );
};
